import React from 'react';

import classNames from 'classnames';

import { figure, money, statusChipClass, STATUS_PENDING } from './changeRequestFormat';
import FundingCell from './FundingCell';

/**
 * One report row. Its funding cell renders the split the server calculated.
 */
export default function ChangeRequestRow({ row, showLocation, onOpen, onDelete, deleting }) {
    // The pop-up is this report's whole point, so the row that opens it has to be
    // reachable from the keyboard: a bare onClick on a <tr> is mouse-only.
    const open = () => onOpen(row);

    return (
        <tr
            className="bc-row--clickable"
            role="button"
            tabIndex={0}
            aria-label={$t('View request {{reference}}', { reference: row.reference })}
            onClick={open}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    open();
                }
            }}
        >
            {showLocation && (
                <td>
                    {row.location_label}
                    <span className="bc-sub">{row.location_group_label}</span>
                </td>
            )}

            <td className="tw-whitespace-nowrap tw-tabular-nums">{row.date}</td>

            <td>
                {/* The change type is a fixed vocabulary and reads as one label, so it
                    never wraps; the free-text description beneath it is what gives. */}
                <span className="tw-whitespace-nowrap">{row.change_label}</span>
                <span className="bc-sub">{row.description}</span>
            </td>

            <td className="tw-whitespace-nowrap">{row.category_label}</td>

            <td className="tw-whitespace-nowrap">{row.requested_by}</td>

            <td className="bc-num tw-whitespace-nowrap">
                {figure(row.original_amount)} &rarr; {figure(row.new_amount)}
                <span className="bc-sub">
                    {row.action_type === 'decrease'
                        ? $t('Total reduction {{amount}}', { amount: money(-Number(row.total_amount ?? 0)) })
                        : $t('Total requested {{amount}}', { amount: money(row.total_amount) })}
                </span>
            </td>

            <FundingCell funding={row.funding || {}} category={row.category} />

            <td>
                <span className={statusChipClass(row.status)}>{row.status_label}</span>
                {row.approved_at && <span className="bc-sub">{row.approved_at}</span>}
            </td>

            <td className="tw-whitespace-nowrap">
                {row.approval?.total_count > 0 ? (
                    <span className="tw-inline-flex tw-items-center tw-gap-2">
                        <span className="bc-avatars">
                            {row.approval.levels.map((level) => (
                                <span
                                    key={level.level}
                                    className={classNames('bc-avatar', level.status === 'approved' ? 'bc-avatar--done' : 'bc-avatar--todo')}
                                    title={level.name}
                                >
                                    {level.initials}
                                </span>
                            ))}
                        </span>
                        <span className="bc-sub tw-inline">
                            {$t('{{approved}} of {{total}}', { approved: row.approval.approved_count, total: row.approval.total_count })}
                        </span>
                    </span>
                ) : (
                    <span className="bc-dim">&mdash;</span>
                )}
            </td>

            <td className="tw-text-end">
                {row.delete_url && row.status === STATUS_PENDING && (
                    // The row itself opens the pop-up, so Delete has to stop the click
                    // reaching it.
                    <button
                        type="button"
                        className="bc-del"
                        disabled={deleting}
                        onClick={(event) => {
                            event.stopPropagation();
                            onDelete(row);
                        }}
                    >
                        {$t('Delete')}
                    </button>
                )}
            </td>
        </tr>
    );
}
